import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Download, Trash2, Upload, Wallet, Check, Coins, AlertTriangle, BarChart3 } from "lucide-react";
import { api, API } from "../../api/client";
import { flatClients } from "../../lib/clientList";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";

const METHOD_LABELS = { cash: "Gotówka", transfer: "Przelew", card: "Karta", other: "Inne" };
// Te same barwy co reszta interfejsu (fioletowa paleta marki), nie domyślne
// kolory recharts.
const CHART_COLORS = ["#7C6EAF", "#a78bfa", "#c4b5fd", "#6366f1", "#8b5cf6"];

const STATUS_LABELS = { draft: "Szkic", approved: "Zatwierdzone", paid: "Wypłacone" };
const STATUS_VARIANT = { draft: "secondary", approved: "default", paid: "outline" };
const COMP_TYPE_LABELS = { hourly: "Godzinowe", percentage: "Procent od sesji", mixed: "Mieszane" };
const DOC_TYPE_LABELS = { contract: "Umowa", medical: "Zaświadczenie", invoice: "Faktura", other: "Inny" };

// Waluta przychodzi z rozliczenia (backend bierze ją z konfiguracji firmy),
// więc formatowanie nie zna żadnego kodu waluty na sztywno.
const money = (v, currency) => `${Number(v || 0).toFixed(2)}${currency ? ` ${currency}` : ""}`;

// Pierwszy i ostatni dzień poprzedniego miesiąca — okres, który rozlicza się
// najczęściej, więc formularz otwiera się od razu na nim.
const lastMonth = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

// Finanse trenera: stawki, rozliczenia, dokumenty klienta, raport postępów.
// Backend tego modułu istniał od dawna bez żadnego interfejsu (R33).
const FinanceTab = ({ token, clients, currentUserId }) => {
  const [compTrainer, setCompTrainer] = useState("");
  const [comp, setComp] = useState(null);
  const [compForm, setCompForm] = useState({
    type: "hourly", hourly_rate: "", percentage: "", base_rate: "",
    vat_rate: "", rates_are_gross: false,
  });
  const [settlements, setSettlements] = useState([]);
  const [members, setMembers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [period, setPeriod] = useState(() => lastMonth());
  const [genTrainer, setGenTrainer] = useState("");
  const [docClient, setDocClient] = useState("");
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("contract");
  const [reportClient, setReportClient] = useState("");
  const [currency, setCurrency] = useState("");
  const [overdue, setOverdue] = useState([]);
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("monthly");
  const [reportLocation, setReportLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const people = flatClients(clients);
  // "— moje —" pokrywa zalogowanego, więc nie ma go na liście drugi raz.
  const otherTrainers = members.filter((m) => m.id !== currentUserId);

  const loadSettlements = useCallback(async () => {
    try {
      const r = await api("get", "/settlements", null, token);
      const rows = r.data || [];
      setSettlements(rows);
      // Kod waluty przychodzi z rozliczeń; do pierwszego rozliczenia etykiety
      // stawek są bez waluty, zamiast zgadywać jaką firma prowadzi.
      const known = rows.find((x) => x.currency)?.currency;
      if (known) setCurrency(known);
    } catch { setSettlements([]); }
  }, [token]);

  // Pusty formularz. Nie zna żadnej stawki ani procentu — wszystko przychodzi
  // z konfiguracji trenera albo zostaje do wpisania.
  const emptyCompForm = useCallback((vat = "") => ({
    type: "hourly", hourly_rate: "", percentage: "", base_rate: "",
    vat_rate: vat, rates_are_gross: false,
  }), []);

  const loadCompensation = useCallback(async (trainerId) => {
    try {
      const path = trainerId ? `/compensation?trainer_id=${trainerId}` : "/compensation";
      const r = await api("get", path, null, token);
      setComp(r.data || null);
      setCompForm(r.data ? {
        type: r.data.type || "hourly",
        hourly_rate: r.data.hourly_rate ?? "",
        percentage: r.data.percentage ?? "",
        base_rate: r.data.base_rate ?? "",
        vat_rate: r.data.vat_rate === undefined || r.data.vat_rate === null ? "" : String(r.data.vat_rate),
        rates_are_gross: !!r.data.rates_are_gross,
      } : emptyCompForm());
    } catch {
      // Brak konfiguracji to normalny stan początkowy — nie błąd.
      setComp(null);
      setCompForm(emptyCompForm());
    }
  }, [token, emptyCompForm]);

  // Raport i zaległości liczyły się w backendzie od dawna i nikt ich nie oglądał.
  const loadReport = useCallback(async (period, locationId) => {
    try {
      const q = locationId ? `&location_id=${locationId}` : "";
      const r = await api("get", `/settlements/report?period=${period}${q}`, null, token);
      setReport(r.data);
      if (r.data?.currency) setCurrency(r.data.currency);
    } catch { setReport(null); }
  }, [token]);

  const loadOverdue = useCallback(async (locationId) => {
    try {
      const q = locationId ? `?location_id=${locationId}` : "";
      const r = await api("get", `/settlements/overdue${q}`, null, token);
      setOverdue(r.data || []);
    } catch { setOverdue([]); }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("get", "/organizations/members", null, token);
        setMembers(r.data.members || []);
        setLocations(r.data.locations || []);
      } catch { setMembers([]); }
      loadCompensation("");
      loadSettlements();
      loadReport(reportPeriod, reportLocation);
      loadOverdue(reportLocation);
    })();
  }, [token, loadSettlements, loadCompensation, loadReport, loadOverdue, reportPeriod, reportLocation]);

  const saveCompensation = async () => {
    setBusy(true);
    try {
      const payload = {
        trainer_id: compTrainer || null,
        type: compForm.type,
        hourly_rate: compForm.hourly_rate === "" ? null : Number(compForm.hourly_rate),
        percentage: compForm.percentage === "" ? null : Number(compForm.percentage),
        base_rate: compForm.base_rate === "" ? null : Number(compForm.base_rate),
        vat_rate: compForm.vat_rate === "" ? 0 : Number(compForm.vat_rate),
        rates_are_gross: compForm.rates_are_gross,
      };
      const r = await api("post", "/compensation", payload, token);
      setComp(r.data);
      toast.success(compTrainer ? "Zapisano wynagrodzenie trenera" : "Zapisano wynagrodzenie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zapisać");
    } finally { setBusy(false); }
  };

  const generateSettlement = async () => {
    setBusy(true);
    try {
      await api("post", "/settlements/generate", {
        trainer_id: genTrainer || currentUserId,
        period_from: period.from,
        period_to: period.to,
      }, token);
      toast.success("Rozliczenie wygenerowane");
      loadSettlements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wygenerować");
    } finally { setBusy(false); }
  };

  const moveSettlement = async (id, action) => {
    try {
      await api("post", `/settlements/${id}/${action}`, null, token);
      toast.success(action === "approve" ? "Zatwierdzone" : "Oznaczone jako wypłacone");
      loadSettlements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się");
    }
  };

  // PDF-y idą przez fetch, bo potrzebują nagłówka autoryzacji — zwykły link
  // do endpointu poleciałby bez tokenu i wrócił 401.
  const downloadFile = async (path, filename) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail.detail || "Nie udało się pobrać pliku");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nie udało się pobrać pliku");
    }
  };

  const loadDocs = async (clientId) => {
    try {
      const r = await api("get", clientId ? `/documents?client_id=${clientId}` : "/documents", null, token);
      setDocs(r.data || []);
    } catch { setDocs([]); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (docClient) form.append("client_id", docClient);
      form.append("doc_type", docType);
      const res = await fetch(`${API}/documents`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail.detail || "Nie udało się wgrać pliku");
        return;
      }
      toast.success("Dokument wgrany");
      loadDocs(docClient);
    } catch {
      toast.error("Nie udało się wgrać pliku");
    } finally { setBusy(false); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Usunąć dokument „${doc.name}"?`)) return;
    try {
      await api("delete", `/documents/${doc.id}`, null, token);
      toast.success("Dokument usunięty");
      loadDocs(docClient);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  return (
    <div className="finance-view">
      <div className="view-header">
        <h1>Finanse</h1>
      </div>

      {/* ===== WYNAGRODZENIE ===== */}
      <Card>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Wallet size={18} />
            <h4 style={{ margin: 0 }}>
              {compTrainer
                ? `Wynagrodzenie: ${members.find((m) => m.id === compTrainer)?.name || ""}`
                : "Moje wynagrodzenie"}
            </h4>
            {comp && <Badge variant="outline">{COMP_TYPE_LABELS[comp.type]}</Badge>}
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            {otherTrainers.length > 0 && (
              <div>
                <Label>Czyje</Label>
                <select value={compTrainer} data-testid="comp-trainer"
                        onChange={(e) => { setCompTrainer(e.target.value); loadCompensation(e.target.value); }}
                        style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 180 }}>
                  <option value="">— moje —</option>
                  {otherTrainers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label>Rodzaj</Label>
              <select value={compForm.type} data-testid="comp-type"
                      onChange={(e) => setCompForm({ ...compForm, type: e.target.value })}
                      style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 170 }}>
                {Object.entries(COMP_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {(compForm.type === "hourly") && (
              <div>
                <Label>Stawka godzinowa{currency ? ` (${currency})` : ""}</Label>
                <Input type="number" value={compForm.hourly_rate} data-testid="comp-hourly"
                       onChange={(e) => setCompForm({ ...compForm, hourly_rate: e.target.value })}
                       style={{ maxWidth: 160 }} />
              </div>
            )}
            {(compForm.type === "mixed") && (
              <div>
                <Label>Stawka bazowa{currency ? ` (${currency}/h)` : " (na godzinę)"}</Label>
                <Input type="number" value={compForm.base_rate}
                       onChange={(e) => setCompForm({ ...compForm, base_rate: e.target.value })}
                       style={{ maxWidth: 160 }} />
              </div>
            )}
            {(compForm.type === "percentage" || compForm.type === "mixed") && (
              <div>
                <Label>Procent od ceny sesji (%)</Label>
                <Input type="number" value={compForm.percentage}
                       onChange={(e) => setCompForm({ ...compForm, percentage: e.target.value })}
                       style={{ maxWidth: 160 }} />
              </div>
            )}
            <div>
              <Label>VAT (%)</Label>
              <Input type="number" value={compForm.vat_rate} data-testid="comp-vat"
                     onChange={(e) => setCompForm({ ...compForm, vat_rate: e.target.value })}
                     style={{ maxWidth: 110 }} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, paddingBottom: 8 }}>
              <input type="checkbox" checked={compForm.rates_are_gross} data-testid="comp-gross"
                     onChange={(e) => setCompForm({ ...compForm, rates_are_gross: e.target.checked })} />
              Stawki powyżej są brutto
            </label>
            <Button onClick={saveCompensation} disabled={busy} data-testid="comp-save">
              <Check size={16} /> Zapisz
            </Button>
          </div>
          <p style={{ fontSize: 12, opacity: 0.65, marginTop: 10, marginBottom: 0 }}>
            VAT 0% znaczy „nie naliczaj" — rozliczenie ma wtedy jedną kwotę.
            Przy stawce większej od zera rozliczenie pokazuje netto, VAT i brutto,
            a do wypłaty idzie brutto. Zaznacz „stawki są brutto", jeśli podane
            wyżej kwoty już VAT zawierają — wtedy podatek jest z nich wyliczany,
            a nie doliczany.
          </p>
        </CardContent>
      </Card>

      {/* ===== ROZLICZENIA ===== */}
      <Card style={{ marginTop: 16 }}>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Coins size={18} /><h4 style={{ margin: 0 }}>Rozliczenia ({settlements.length})</h4>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            {otherTrainers.length > 0 && (
              <div>
                <Label>Trener</Label>
                <select value={genTrainer} data-testid="settle-trainer"
                        onChange={(e) => setGenTrainer(e.target.value)}
                        style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 180 }}>
                  <option value="">— ja —</option>
                  {otherTrainers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label>Od</Label>
              <Input type="date" value={period.from} data-testid="settle-from"
                     onChange={(e) => setPeriod({ ...period, from: e.target.value })} style={{ maxWidth: 170 }} />
            </div>
            <div>
              <Label>Do</Label>
              <Input type="date" value={period.to} data-testid="settle-to"
                     onChange={(e) => setPeriod({ ...period, to: e.target.value })} style={{ maxWidth: 170 }} />
            </div>
            <Button onClick={generateSettlement} disabled={busy} data-testid="settle-generate">
              Generuj rozliczenie
            </Button>
          </div>

          {settlements.length === 0 && (
            <p style={{ opacity: 0.6, fontSize: 13 }}>
              Brak rozliczeń. Rozliczenie zbiera zakończone sesje z wybranego okresu
              i wycenia je według stawek powyżej.
            </p>
          )}

          {settlements.map((s) => (
            <div key={s.id} style={{ borderBottom: "1px solid var(--border,#eee)", padding: "10px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontWeight: 600 }}>
                    {s.period_from} — {s.period_to}
                    {s.trainer_name && <span style={{ opacity: 0.6, fontWeight: 400 }}> · {s.trainer_name}</span>}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {s.sessions_count} sesji · {s.total_hours} h
                    {s.vat_rate > 0
                      ? ` · netto ${money(s.net_amount, s.currency)} + VAT ${s.vat_rate}% = ${money(s.gross_amount, s.currency)}`
                      : ` · ${money(s.amount, s.currency)}`}
                  </div>
                </div>
                <Badge variant={STATUS_VARIANT[s.status] || "secondary"}>{STATUS_LABELS[s.status] || s.status}</Badge>
                {s.status === "draft" && (
                  <Button size="sm" data-testid={`settle-approve-${s.id}`} onClick={() => moveSettlement(s.id, "approve")}>
                    Zatwierdź
                  </Button>
                )}
                {s.status === "approved" && (
                  <Button size="sm" data-testid={`settle-paid-${s.id}`} onClick={() => moveSettlement(s.id, "paid")}>
                    Oznacz jako wypłacone
                  </Button>
                )}
                <Button size="sm" variant="outline" title="Pobierz PDF"
                        onClick={() => downloadFile(`/settlements/${s.id}/pdf`, `rozliczenie_${s.period_from}_${s.period_to}.pdf`)}>
                  <Download size={14} /> PDF
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                  {expanded === s.id ? "Zwiń" : "Sesje"}
                </Button>
              </div>

              {expanded === s.id && (
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  {(s.session_logs || []).map((l) => (
                    <div key={l.session_id} style={{ display: "flex", gap: 12, padding: "3px 0", opacity: 0.85 }}>
                      <span style={{ minWidth: 90 }}>{l.date}</span>
                      <span style={{ flex: 1 }}>{l.client_name}</span>
                      <span>{l.duration_min} min</span>
                      <span style={{ minWidth: 90, textAlign: "right" }}>{money(l.amount, s.currency)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ===== ZALEGŁOŚCI ===== */}
      {overdue.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <CardContent style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <AlertTriangle size={18} />
              <h4 style={{ margin: 0 }}>Zaległości ({overdue.length})</h4>
            </div>
            {overdue.map((o) => (
              <div key={o.id} style={{ display: "flex", gap: 10, padding: "6px 0", flexWrap: "wrap",
                                       borderBottom: "1px solid var(--border,#f0f0f0)", fontSize: 14 }}>
                <span style={{ flex: 1, minWidth: 160, fontWeight: 500 }}>{o.client_name}</span>
                <span style={{ opacity: 0.7 }}>{o.plan_name}</span>
                <span style={{ opacity: 0.7 }}>koniec: {(o.end_date || "").slice(0, 10)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== RAPORT ===== */}
      <Card style={{ marginTop: 16 }}>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <BarChart3 size={18} /><h4 style={{ margin: 0 }}>Przychód</h4>
            {locations.length > 0 && (
              <select value={reportLocation} data-testid="report-location"
                      onChange={(e) => setReportLocation(e.target.value)}
                      style={{ marginLeft: "auto", padding: 6, borderRadius: 6 }}>
                <option value="">Wszystkie lokalizacje</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <select value={reportPeriod} data-testid="report-period"
                    onChange={(e) => setReportPeriod(e.target.value)}
                    style={{ marginLeft: locations.length > 0 ? 0 : "auto", padding: 6, borderRadius: 6 }}>
              <option value="monthly">Miesiąc</option>
              <option value="quarterly">Kwartał</option>
              <option value="yearly">Rok</option>
            </select>
            <Button size="sm" variant="outline" data-testid="report-csv"
                    onClick={() => downloadFile(`/settlements/export?period=${reportPeriod}${reportLocation ? `&location_id=${reportLocation}` : ""}`,
                                                `duofit_finanse_${reportPeriod}.csv`)}>
              <Download size={14} /> CSV
            </Button>
          </div>

          {!report ? (
            <p style={{ opacity: 0.6, fontSize: 13 }}>Brak danych za ten okres.</p>
          ) : (
            <>
              <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                {report.date_from} — {report.date_to} · {report.payment_count} wpłat
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
                {money(report.total_revenue, currency)}
              </div>

              {(report.daily_breakdown || []).length > 1 && (
                <div style={{ height: 140, marginBottom: 16 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={report.daily_breakdown}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" tickFormatter={(d) => d.slice(8, 10)} fontSize={11} />
                      <YAxis fontSize={11} width={40} />
                      <Tooltip formatter={(v) => money(v, currency)} labelFormatter={(d) => d} />
                      <Bar dataKey="total" fill={CHART_COLORS[0]} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {Object.keys(report.method_breakdown || {}).length > 1 && (
                <div style={{ height: 160, marginBottom: 16, display: "flex", alignItems: "center" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={Object.entries(report.method_breakdown).map(([method, total]) => ({
                          name: METHOD_LABELS[method] || method, value: total,
                        }))}
                        dataKey="value" nameKey="name" innerRadius={40} outerRadius={65}
                      >
                        {Object.keys(report.method_breakdown).map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => money(v, currency)} />
                      <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Sens tylko przy "Wszystkie lokalizacje" — przy filtrze na jedną
                  lokalizację byłby to wykres kołowy z jednym wycinkiem. */}
              {!reportLocation && (report.by_location || []).length > 1 && (
                <div style={{ height: 160, marginBottom: 16, display: "flex", alignItems: "center" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={report.by_location.map((l) => ({ name: l.location_name, value: l.total }))}
                        dataKey="value" nameKey="name" innerRadius={40} outerRadius={65}
                      >
                        {report.by_location.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => money(v, currency)} />
                      <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {(report.client_breakdown || []).slice(0, 10).map((c) => (
                <div key={c.client_name} style={{ display: "flex", gap: 10, padding: "4px 0", fontSize: 14 }}>
                  <span style={{ flex: 1 }}>{c.client_name}</span>
                  <span style={{ opacity: 0.6 }}>{c.count}×</span>
                  <span style={{ minWidth: 110, textAlign: "right" }}>{money(c.total, currency)}</span>
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {/* ===== DOKUMENTY ===== */}
      <Card style={{ marginTop: 16 }}>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FileText size={18} /><h4 style={{ margin: 0 }}>Dokumenty klienta</h4>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <Label>Klient</Label>
              <select value={docClient} data-testid="doc-client"
                      onChange={(e) => { setDocClient(e.target.value); loadDocs(e.target.value); }}
                      style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 200 }}>
                <option value="">— wybierz —</option>
                {people.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Rodzaj</Label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                      style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 150 }}>
                {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <Button variant="outline" disabled={busy || !docClient} data-testid="doc-upload"
                    onClick={() => fileInput.current?.click()}>
              <Upload size={16} /> Wgraj plik
            </Button>
            <input ref={fileInput} type="file" data-testid="doc-file" style={{ display: "none" }}
                   accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                   onChange={(e) => { uploadDoc(e.target.files?.[0]); e.target.value = ""; }} />
          </div>

          {docClient && docs.length === 0 && (
            <p style={{ opacity: 0.6, fontSize: 13, marginTop: 12 }}>Ten klient nie ma jeszcze dokumentów.</p>
          )}
          {docs.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0",
                                     borderBottom: "1px solid var(--border,#f0f0f0)" }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <span style={{ fontSize: 14 }}>{d.name}</span>
                <span style={{ fontSize: 12, opacity: 0.6 }}> · {DOC_TYPE_LABELS[d.type] || d.type}</span>
              </div>
              <Button size="sm" variant="outline" onClick={() => downloadFile(`/documents/${d.id}/download`, d.name)}>
                <Download size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteDoc(d)}>
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* ===== RAPORT POSTĘPÓW ===== */}
      <Card style={{ marginTop: 16 }}>
        <CardContent style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FileText size={18} /><h4 style={{ margin: 0 }}>Raport postępów (PDF)</h4>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <Label>Klient</Label>
              <select value={reportClient} data-testid="report-client"
                      onChange={(e) => setReportClient(e.target.value)}
                      style={{ display: "block", padding: 8, borderRadius: 6, minWidth: 200 }}>
                <option value="">— wybierz —</option>
                {people.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Button disabled={!reportClient} data-testid="report-download"
                    onClick={() => downloadFile(`/reports/client/${reportClient}/progress`, `raport_postepow.pdf`)}>
              <Download size={16} /> Pobierz raport
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinanceTab;
